import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Funnel, LabelList, Cell } from 'recharts';
import { TrendingUp, Users, Activity, CheckCircle, AlertCircle, Filter, Download, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { analyticsService } from '../../services/analyticsService';
import { companyService } from '../../services/companyService';

export default function AnalyticsDashboard() {
  const { companyIdentifier } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);

  // Analytics Data
  const [metrics, setMetrics] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [staffActivity, setStaffActivity] = useState([]);

  // Filters
  const [dateRange, setDateRange] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('sessions');

  // Real-time subscription refs
  const metricsSubscription = useRef(null);
  const sessionsSubscription = useRef(null);
  const conversionsSubscription = useRef(null);
  const activitySubscription = useRef(null);

  // Fetch company data
  useEffect(() => {
    if (companyIdentifier) {
      loadCompanyData();
    }
  }, [companyIdentifier]);

  // Fetch analytics data
  useEffect(() => {
    if (company?.id) {
      loadAnalyticsData();
    }
  }, [company, dateRange]);

  // Setup real-time subscriptions
  useEffect(() => {
    if (company?.id && isRealTimeActive) {
      setupRealtimeSubscriptions();
    }

    return () => {
      cleanupSubscriptions();
    };
  }, [company?.id, isRealTimeActive, dateRange]);

  const setupRealtimeSubscriptions = () => {
    // Subscribe to metrics updates
    metricsSubscription.current = analyticsService?.subscribeToMetricsUpdates(
      company?.id,
      handleMetricsUpdate
    );

    // Subscribe to session updates
    sessionsSubscription.current = analyticsService?.subscribeToSessionUpdates(
      company?.id,
      handleSessionUpdate
    );

    // Subscribe to conversion updates
    conversionsSubscription.current = analyticsService?.subscribeToConversionUpdates(
      company?.id,
      handleConversionUpdate
    );

    // Subscribe to staff activity updates
    activitySubscription.current = analyticsService?.subscribeToStaffActivityUpdates(
      company?.id,
      handleActivityUpdate
    );

    console.log('Real-time subscriptions established');
  };

  const cleanupSubscriptions = () => {
    metricsSubscription?.current?.unsubscribe?.();
    sessionsSubscription?.current?.unsubscribe?.();
    conversionsSubscription?.current?.unsubscribe?.();
    activitySubscription?.current?.unsubscribe?.();
    console.log('Real-time subscriptions cleaned up');
  };

  const handleMetricsUpdate = async (data, eventType) => {
    console.log('Metrics update received:', eventType, data);
    
    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      // Reload metrics and trend data to reflect changes
      await loadMetricsAndTrends();
    }
  };

  const handleSessionUpdate = async (data, eventType) => {
    console.log('Session update received:', eventType, data);
    
    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      // Reload metrics as sessions affect overall counts
      await loadMetricsAndTrends();
    }
  };

  const handleConversionUpdate = async (data, eventType) => {
    console.log('Conversion update received:', eventType, data);
    
    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      // Reload funnel and metrics data
      await loadConversionData();
    }
  };

  const handleActivityUpdate = (data) => {
    console.log('Staff activity update received:', data);
    
    // Prepend new activity to the list
    setStaffActivity(prev => [data, ...prev]?.slice(0, 10));
  };

  const loadMetricsAndTrends = async () => {
    try {
      const [metricsData, trendsData] = await Promise.all([
        analyticsService?.getDashboardMetrics(company?.id, dateRange),
        analyticsService?.getTrendData(company?.id, selectedMetric, 7)
      ]);
      setMetrics(metricsData);
      setTrendData(trendsData);
    } catch (err) {
      console.error('Error loading metrics:', err);
    }
  };

  const loadConversionData = async () => {
    try {
      const [metricsData, funnelDataResult] = await Promise.all([
        analyticsService?.getDashboardMetrics(company?.id, dateRange),
        analyticsService?.getConversionFunnel(company?.id, dateRange)
      ]);
      setMetrics(metricsData);
      setFunnelData(funnelDataResult);
    } catch (err) {
      console.error('Error loading conversion data:', err);
    }
  };

  const loadCompanyData = async () => {
    try {
      const companyData = await companyService?.getBySlug(companyIdentifier);
      setCompany(companyData);
    } catch (err) {
      setError('Failed to load company data');
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      const [metricsData, trendsData, funnelDataResult, activityData] = await Promise.all([
        analyticsService?.getDashboardMetrics(company?.id, dateRange),
        analyticsService?.getTrendData(company?.id, selectedMetric, 7),
        analyticsService?.getConversionFunnel(company?.id, dateRange),
        analyticsService?.getStaffActivity(company?.id, 10)
      ]);

      setMetrics(metricsData);
      setTrendData(trendsData);
      setFunnelData(funnelDataResult);
      setStaffActivity(activityData);
    } catch (err) {
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalyticsData();
    setRefreshing(false);
  };

  const toggleRealTime = () => {
    setIsRealTimeActive(prev => !prev);
  };

  const handleExport = () => {
    const csvContent = generateCSVReport();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const generateCSVReport = () => {
    const headers = ['Date', 'Sessions', 'Visitors', 'Conversions', 'Conversion Rate'];
    const rows = trendData?.map(d => [d?.date, d?.sessions, d?.visitors, d?.conversions, d?.conversionRate]);
    return [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
  };

  // Calculate summary metrics from data
  const summaryMetrics = metrics?.length > 0 ? {
    totalSessions: metrics?.reduce((sum, m) => sum + m?.totalSessions, 0),
    uniqueVisitors: metrics?.reduce((sum, m) => sum + m?.uniqueVisitors, 0),
    totalConversions: metrics?.reduce((sum, m) => sum + m?.totalConversions, 0),
    avgConversionRate: (metrics?.reduce((sum, m) => sum + parseFloat(m?.conversionRate), 0) / metrics?.length)?.toFixed(2)
  } : { totalSessions: 0, uniqueVisitors: 0, totalConversions: 0, avgConversionRate: 0 };

  const FUNNEL_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  if (loading && !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error && !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle className="w-6 h-6" />
            <h3 className="text-lg font-semibold">Error</h3>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/client-dashboard-home')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-1 text-gray-600">{company?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleRealTime}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isRealTimeActive 
                    ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100' :'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {isRealTimeActive ? (
                  <>
                    <Wifi className="w-4 h-4" />
                    <span className="hidden sm:inline">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4" />
                    <span className="hidden sm:inline">Disconnected</span>
                  </>
                )}
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
          {isRealTimeActive && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Real-time updates active - Dashboard will refresh automatically</span>
            </div>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
            </select>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="sessions">Sessions</option>
              <option value="visitors">Unique Visitors</option>
              <option value="conversions">Conversions</option>
            </select>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Total Sessions</h3>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summaryMetrics?.totalSessions?.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Unique Visitors</h3>
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summaryMetrics?.uniqueVisitors?.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Conversions</h3>
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summaryMetrics?.totalConversions?.toLocaleString()}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Conversion Rate</h3>
              <Activity className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{summaryMetrics?.avgConversionRate}%</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Trend Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Trends</h3>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : trendData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sessions" stroke="#3B82F6" strokeWidth={2} />
                  <Line type="monotone" dataKey="visitors" stroke="#10B981" strokeWidth={2} />
                  <Line type="monotone" dataKey="conversions" stroke="#8B5CF6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No trend data available
              </div>
            )}
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Funnel</h3>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : funnelData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnelData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="step" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6">
                    {funnelData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={FUNNEL_COLORS?.[index % FUNNEL_COLORS?.length]} />
                    ))}
                    <LabelList dataKey="percentage" position="right" formatter={(value) => `${value}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No funnel data available
              </div>
            )}
          </div>
        </div>

        {/* Staff Activity Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Staff Activity
          </h3>
          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : staffActivity?.length > 0 ? (
            <div className="space-y-3">
              {staffActivity?.map((activity) => (
                <div key={activity?.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity?.actionDescription}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {activity?.userFullName} • {new Date(activity.createdAt)?.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No activity recorded</p>
          )}
        </div>

        {/* Actionable Insights */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Actionable Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Peak Usage Times</h4>
              <p className="text-sm text-gray-600">
                Highest traffic occurs during business hours. Consider promoting special offers during off-peak times.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Mobile Optimization</h4>
              <p className="text-sm text-gray-600">
                {metrics?.length > 0 && metrics?.[0]?.mobileSessions > metrics?.[0]?.desktopSessions ? 
                  'Mobile users dominate traffic. Ensure visualizer is fully mobile-optimized.' :
                  'Desktop users are primary audience. Focus on large-screen experience.'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Conversion Opportunity</h4>
              <p className="text-sm text-gray-600">
                {funnelData?.length > 0 && parseFloat(funnelData?.[funnelData?.length - 1]?.percentage) < 50 ?
                  'Many users drop off before reference code. Simplify the final steps.' :
                  'Strong completion rates. Consider adding upsell opportunities.'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Content Strategy</h4>
              <p className="text-sm text-gray-600">
                Update popular color options based on selection frequency to improve user experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}